import React from 'react';
import { X } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface SettingsModalProps {
  isOpen: boolean;
  enableRapidScanning: boolean;
  onEnableRapidScanningChange: (value: boolean) => void;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  enableRapidScanning,
  onEnableRapidScanningChange,
  onClose
}) => {
  const { t } = useTranslation();
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-2xl sm:rounded-lg max-w-md w-full sm:h-auto h-auto animate-slide-up sm:animate-none">
        <div className="p-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="text-base sm:text-lg font-semibold">{t('common.buttons.settings')}</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 p-2 min-h-[44px] min-w-[44px] flex items-center justify-center -mr-2"
            aria-label={t('common.buttons.close')}
          >
            <X size={20} />
          </button>
        </div>
        <div className="p-4 sm:p-6">
          <div className="space-y-4">
            {/* Rapid scanning toggle */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex-1">
                <h4 className="text-sm font-medium text-gray-900">
                  {t('warehouse.modules.inventory.settings.rapidScanning')}
                </h4>
                <p className="text-xs sm:text-sm text-gray-500 mt-1">
                  {t('warehouse.modules.inventory.settings.rapidScanningDescription')}
                </p>
              </div>
              <button
                onClick={() => onEnableRapidScanningChange(!enableRapidScanning)}
                className={`relative inline-flex h-8 w-14 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  enableRapidScanning ? 'bg-blue-600' : 'bg-gray-200'
                }`}
                role="switch"
                aria-checked={enableRapidScanning}
                aria-label={t('warehouse.modules.inventory.settings.toggleRapidScanning')}
              >
                <span
                  className={`pointer-events-none inline-block h-7 w-7 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    enableRapidScanning ? 'translate-x-6 sm:translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </div>
        <div className="p-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="w-full sm:w-auto px-4 py-2.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm min-h-[44px]"
          >
            {t('common.buttons.close')}
          </button>
        </div>
      </div>
    </div>
  );
};
